import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Boxes, Download, Link2, Plus, Settings2, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { downloadCsv } from '@/features/sales/api';
import { useDebounce } from '@/hooks/use-debounce';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';

// ── Types & API ──────────────────────────────────────────────────────────────

type FieldType = 'text' | 'textarea' | 'number' | 'money' | 'date' | 'select' | 'multiselect' | 'checkbox' | 'email' | 'phone' | 'url';
type Field = { key: string; label: string; type: FieldType; required: boolean; options?: string[]; showInList: boolean };
type RecordType = 'lead' | 'contact' | 'company' | 'deal';
type CustomObject = {
  id: string;
  key: string;
  singular: string;
  plural: string;
  description: string | null;
  icon: string;
  fields: Field[];
  associations: RecordType[];
  isActive: boolean;
  recordCount?: number;
};
type ObjectRecord = { id: string; name: string; data: Record<string, unknown>; ownerId: string | null; owner: { id: string; name: string } | null; updatedAt: string; createdAt: string; linkCount?: number };

const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Short text',
  textarea: 'Long text',
  number: 'Number',
  money: 'Amount (AED)',
  date: 'Date',
  select: 'Dropdown',
  multiselect: 'Several choices',
  checkbox: 'Yes / no',
  email: 'Email',
  phone: 'Phone',
  url: 'Link',
};
const RECORD_LABELS: Record<RecordType, string> = { lead: 'Leads', contact: 'Contacts', company: 'Companies', deal: 'Deals' };
const recordPath = (type: string, id: string) => (type === 'deal' ? `/crm/deals/${id}` : `/crm/${type === 'company' ? 'companies' : `${type}s`}/${id}`);

function useObjects() {
  return useQuery({ queryKey: ['objects'], queryFn: () => apiGet<CustomObject[]>('/objects') });
}

/** How a stored value reads in a table or on a card. */
export function displayValue(field: Pick<Field, 'type'>, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (field.type === 'checkbox') return value ? 'Yes' : 'No';
  if (field.type === 'money') return `AED ${Number(value).toLocaleString('en-AE', { minimumFractionDigits: 2 })}`;
  if (field.type === 'date') return formatDate(String(value));
  return String(value);
}

// ── /objects ─────────────────────────────────────────────────────────────────

/** Custom objects (HubSpot): the record types this workspace defines. */
export function ObjectsPage() {
  const { data: objects = [], isLoading } = useObjects();
  const [editing, setEditing] = useState<CustomObject | 'new' | null>(null);
  const { can } = usePermission();

  return (
    <div>
      <PageHeader
        title="Custom objects"
        description="Your own record types — properties, vehicles, units, policies — with their own fields, linked to leads, contacts, companies and deals."
        breadcrumbs={[{ label: 'CRM' }, { label: 'Custom objects' }]}
        actions={
          <Can permission="admin.customfield.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> New object
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : objects.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <Boxes className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No custom objects yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            A real-estate agency might add “Properties”, a car dealer “Vehicles”, an insurer “Policies”.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {objects.map((object) => (
            <Card key={object.id} className={cn(!object.isActive && 'opacity-60')}>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/objects/${object.key}`} className="text-lg font-bold hover:underline">
                    {object.plural}
                  </Link>
                  {!object.isActive && <Badge variant="secondary">Off</Badge>}
                </div>
                {object.description && <p className="line-clamp-2 text-sm text-muted-foreground">{object.description}</p>}
                <p className="text-xs text-muted-foreground">
                  {object.recordCount} records · {object.fields.length} fields
                  {object.associations.length ? ` · linked to ${object.associations.map((type) => RECORD_LABELS[type].toLowerCase()).join(', ')}` : ''}
                </p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/objects/${object.key}`}>Open</Link>
                  </Button>
                  {can('admin.customfield.update') && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(object)}>
                      <Settings2 /> Fields
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {editing && <ObjectBuilder object={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ObjectBuilder({ object, onClose }: { object: CustomObject | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [singular, setSingular] = useState(object?.singular ?? 'Property');
  const [plural, setPlural] = useState(object?.plural ?? 'Properties');
  const [key, setKey] = useState(object?.key ?? 'properties');
  const [description, setDescription] = useState(object?.description ?? '');
  const [associations, setAssociations] = useState<RecordType[]>(object?.associations ?? ['contact', 'deal']);
  const [fields, setFields] = useState<Field[]>(
    object?.fields ?? [
      { key: 'name', label: 'Name', type: 'text', required: true, showInList: true },
      { key: 'price', label: 'Price', type: 'money', required: false, showInList: true },
      { key: 'type', label: 'Type', type: 'select', required: false, options: ['Apartment', 'Villa', 'Townhouse', 'Office'], showInList: true },
      { key: 'community', label: 'Community', type: 'text', required: false, showInList: true },
    ],
  );
  const [isActive, setIsActive] = useState(object?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const update = (index: number, patch: Partial<Field>) => setFields((current) => current.map((field, at) => (at === index ? { ...field, ...patch } : field)));
  const move = (index: number, by: number) =>
    setFields((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(index + by, 0, item);
      return next;
    });

  const save = useMutation({
    mutationFn: () => {
      const body = { key, singular, plural, description: description || null, associations, fields, isActive, icon: 'box' };
      return object ? apiPatch<CustomObject>(`/objects/${object.key}`, body) : apiPost<CustomObject>('/objects', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['objects'] });
      toast.success('Saved');
      onClose();
      if (!object) navigate(`/objects/${saved.key}`);
    },
    onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not save'),
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/objects/${object?.key}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['objects'] });
      onClose();
    },
    onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not delete'),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{object ? `${object.plural} — fields` : 'New object'}</DialogTitle>
          <DialogDescription>The first field is each record’s name.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>One is called</Label>
              <Input value={singular} onChange={(event) => setSingular(event.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Many are called</Label>
              <Input
                value={plural}
                onChange={(event) => {
                  setPlural(event.target.value);
                  if (!object) setKey(event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>Address</Label>
              <div className="flex items-center rounded-lg border ps-2 text-sm">
                <span className="text-muted-foreground">/objects/</span>
                <input disabled={Boolean(object)} className="h-10 min-w-0 flex-1 bg-transparent px-1 outline-none disabled:opacity-60" value={key} onChange={(event) => setKey(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
              </div>
            </div>
          </div>
          <Textarea rows={2} placeholder="What it is for (optional)" value={description} onChange={(event) => setDescription(event.target.value)} />
          <div className="space-y-1.5">
            <Label>Can be linked to</Label>
            <div className="flex flex-wrap gap-4">
              {(Object.keys(RECORD_LABELS) as RecordType[]).map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={associations.includes(type)} onCheckedChange={(value) => setAssociations(value ? [...associations, type] : associations.filter((item) => item !== type))} />
                  {RECORD_LABELS[type]}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fields</Label>
            {fields.map((field, index) => {
              const saved = object?.fields.some((item) => item.key === field.key);
              return (
                <div key={index} className="space-y-2 rounded-xl border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      className="min-w-[10rem] flex-1"
                      value={field.label}
                      onChange={(event) => update(index, { label: event.target.value, ...(saved ? {} : { key: event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || field.key }) })}
                    />
                    <Select className="w-40" value={field.type} disabled={index === 0} onChange={(event) => update(index, { type: event.target.value as FieldType })}>
                      {Object.entries(TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <label className="flex items-center gap-1 text-xs">
                      <Checkbox checked={field.required} onCheckedChange={(value) => update(index, { required: Boolean(value) })} /> Required
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <Checkbox checked={field.showInList} onCheckedChange={(value) => update(index, { showInList: Boolean(value) })} /> In list
                    </label>
                    {index > 0 && (
                      <>
                        <Button size="sm" variant="ghost" disabled={index === 1} onClick={() => move(index, -1)}>
                          <ArrowUp />
                        </Button>
                        <Button size="sm" variant="ghost" disabled={index === fields.length - 1} onClick={() => move(index, 1)}>
                          <ArrowDown />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setFields((current) => current.filter((_, at) => at !== index))}>
                          <Trash2 />
                        </Button>
                      </>
                    )}
                  </div>
                  {(field.type === 'select' || field.type === 'multiselect') && (
                    <Input
                      placeholder="Options, separated by commas"
                      value={(field.options ?? []).join(', ')}
                      onChange={(event) => update(index, { options: event.target.value.split(',').map((option) => option.trim()).filter(Boolean) })}
                    />
                  )}
                </div>
              );
            })}
            <Button size="sm" variant="outline" onClick={() => setFields((current) => [...current, { key: `field_${current.length + 1}`, label: 'New field', type: 'text', required: false, showInList: false }])}>
              <Plus /> Add field
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isActive} onCheckedChange={setIsActive} /> Switched on
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter className={object ? 'sm:justify-between' : undefined}>
          {object && (
            <Can permission="admin.customfield.delete">
              <Button variant="ghost" className="text-destructive" onClick={() => window.confirm(`Delete ${object.plural}?`) && remove.mutate()}>
                <Trash2 /> Delete object
              </Button>
            </Can>
          )}
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── /objects/:key ────────────────────────────────────────────────────────────

export function ObjectRecordsPage() {
  const { objectKey = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { can } = usePermission();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const debounced = useDebounce(q);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['objects', objectKey, 'records', debounced, page],
    queryFn: () => apiGet<{ object: CustomObject; items: ObjectRecord[]; total: number; limit: number }>(`/objects/${objectKey}/records`, { params: { q: debounced || undefined, page } }),
    placeholderData: (previous) => previous,
  });
  const [editing, setEditing] = useState<ObjectRecord | 'new' | null>(null);
  const openId = params.get('open');

  if (isError) return <p className="text-sm text-muted-foreground">This object does not exist.</p>;
  if (isLoading || !data) return <PageLoader />;
  const { object } = data;
  const columns = object.fields.filter((field, index) => index > 0 && field.showInList).slice(0, 5);
  const pages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div>
      <PageHeader
        title={object.plural}
        description={object.description ?? undefined}
        breadcrumbs={[{ label: 'Custom objects', to: '/objects' }, { label: object.plural }]}
        actions={
          <div className="flex gap-2">
            {can('crm.object.export') && (
              <Button variant="outline" onClick={() => void downloadCsv(`/objects/${object.key}/export`, `${object.key}.csv`)}>
                <Download /> Export
              </Button>
            )}
            {can('crm.object.create') && object.isActive && (
              <Button onClick={() => setEditing('new')}>
                <Plus /> New {object.singular.toLowerCase()}
              </Button>
            )}
          </div>
        }
      />
      <Input className="mb-4 max-w-sm" placeholder={`Search ${object.plural.toLowerCase()}…`} value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} />
      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {data.items.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No {object.plural.toLowerCase()} yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{object.fields[0].label}</TableHead>
                  {columns.map((field) => (
                    <TableHead key={field.key}>{field.label}</TableHead>
                  ))}
                  <TableHead>Owner</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row) => (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => setParams({ open: row.id })}>
                    <TableCell className="font-medium">
                      {row.name}
                      {row.linkCount ? <span className="ms-2 text-xs text-muted-foreground">· {row.linkCount} linked</span> : null}
                    </TableCell>
                    {columns.map((field) => (
                      <TableCell key={field.key} className="text-sm">
                        {displayValue(field, row.data[field.key])}
                      </TableCell>
                    ))}
                    <TableCell className="text-sm text-muted-foreground">{row.owner?.name ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{timeAgo(row.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-muted-foreground">
                {page} / {pages}
              </span>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      {editing && <RecordForm object={object} record={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {openId && !editing && <RecordDialog object={object} id={openId} onEdit={(record) => setEditing(record)} onClose={() => setParams({})} />}
    </div>
  );
}

function FieldInput({ field, value, onChange }: { field: Field; value: unknown; onChange: (value: unknown) => void }) {
  switch (field.type) {
    case 'textarea':
      return <Textarea rows={3} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} />;
    case 'select':
      return (
        <Select value={String(value ?? '')} onChange={(event) => onChange(event.target.value)}>
          <option value="">—</option>
          {field.options?.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </Select>
      );
    case 'multiselect': {
      const chosen = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {field.options?.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onChange(chosen.includes(option) ? chosen.filter((item) => item !== option) : [...chosen, option])}
              className={cn('rounded-full border px-3 py-1 text-xs', chosen.includes(option) ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent')}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }
    case 'checkbox':
      return (
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={Boolean(value)} onCheckedChange={onChange} /> Yes
        </label>
      );
    default:
      return (
        <Input
          type={field.type === 'number' || field.type === 'money' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
          step={field.type === 'money' ? '0.01' : undefined}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

function RecordForm({ object, record, onClose }: { object: CustomObject; record: ObjectRecord | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>(record?.data ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => (record ? apiPatch(`/objects/${object.key}/records/${record.id}`, { data: values }) : apiPost(`/objects/${object.key}/records`, { data: values })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['objects'] });
      toast.success('Saved');
      onClose();
    },
    onError: (failure) => {
      if (failure instanceof ApiRequestError) {
        setErrors(failure.fieldErrors);
        setError(failure.message);
      } else setError('Could not save');
    },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{record ? `Edit ${record.name}` : `New ${object.singular.toLowerCase()}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {object.fields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label>
                {field.label}
                {field.required && <span className="text-destructive"> *</span>}
              </Label>
              <FieldInput field={field} value={values[field.key]} onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))} />
              {errors[field.key] && <p className="text-xs text-destructive">{errors[field.key]}</p>}
            </div>
          ))}
          <FormError message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordDialog({ object, id, onEdit, onClose }: { object: CustomObject; id: string; onEdit: (record: ObjectRecord) => void; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const { data } = useQuery({
    queryKey: ['objects', object.key, 'record', id],
    queryFn: () => apiGet<{ record: ObjectRecord; links: { entityType: RecordType; entityId: string; name: string }[] }>(`/objects/${object.key}/records/${id}`),
  });
  const [linkType, setLinkType] = useState<RecordType | ''>(object.associations[0] ?? '');
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search);
  const { data: options = [] } = useQuery({
    queryKey: ['objects', 'link-search', linkType, debounced],
    queryFn: async () => {
      const path = { lead: '/leads', contact: '/contacts', company: '/companies', deal: '/deals' }[linkType as RecordType];
      const result = await apiGet<unknown>(path, { params: { q: debounced || undefined, limit: 8 } });
      const items = (Array.isArray(result) ? result : ((result as { items?: unknown[] }).items ?? [])) as Record<string, string>[];
      return items.map((item) => ({ id: item.id, name: item.name ?? item.title ?? [item.firstName, item.lastName].filter(Boolean).join(' ') }));
    },
    enabled: Boolean(linkType) && debounced.length > 0,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['objects', object.key, 'record', id] });
    void queryClient.invalidateQueries({ queryKey: ['objects', object.key, 'records'] });
  };
  const link = useMutation({
    mutationFn: (entityId: string) => apiPost(`/objects/${object.key}/records/${id}/links`, { entityType: linkType, entityId }),
    onSuccess: () => {
      setSearch('');
      refresh();
    },
    onError: (failure) => toast.error(failure instanceof ApiRequestError ? failure.message : 'Could not link'),
  });
  const unlink = useMutation({
    mutationFn: (target: { entityType: string; entityId: string }) => apiPost(`/objects/${object.key}/records/${id}/unlink`, target),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/objects/${object.key}/records/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['objects'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data?.record.name ?? '…'}</DialogTitle>
          <DialogDescription>
            {object.singular}
            {data?.record.owner ? ` · ${data.record.owner.name}` : ''}
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-5">
            <dl className="grid gap-3 sm:grid-cols-2">
              {object.fields.slice(1).map((field) => (
                <div key={field.key}>
                  <dt className="text-xs text-muted-foreground">{field.label}</dt>
                  <dd className="whitespace-pre-wrap text-sm font-medium">
                    {field.type === 'url' && data.record.data[field.key] ? (
                      <a className="text-primary underline" href={String(data.record.data[field.key])} target="_blank" rel="noreferrer">
                        {String(data.record.data[field.key])}
                      </a>
                    ) : (
                      displayValue(field, data.record.data[field.key])
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            {object.associations.length > 0 && (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <Link2 className="h-4 w-4" /> Linked records
                </p>
                <div className="flex flex-wrap gap-2">
                  {data.links.map((item) => (
                    <span key={`${item.entityType}-${item.entityId}`} className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
                      <Link to={recordPath(item.entityType, item.entityId)} className="hover:underline">
                        {item.name}
                      </Link>
                      <span className="text-xs text-muted-foreground">{item.entityType}</span>
                      {can('crm.object.update') && (
                        <button onClick={() => unlink.mutate({ entityType: item.entityType, entityId: item.entityId })}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {data.links.length === 0 && <p className="text-sm text-muted-foreground">Not linked yet.</p>}
                </div>
                {can('crm.object.update') && (
                  <div className="flex flex-wrap gap-2">
                    <Select className="w-36" value={linkType} onChange={(event) => setLinkType(event.target.value as RecordType)}>
                      {object.associations.map((type) => (
                        <option key={type} value={type}>
                          {RECORD_LABELS[type]}
                        </option>
                      ))}
                    </Select>
                    <div className="relative min-w-[14rem] flex-1">
                      <Input placeholder="Search to link…" value={search} onChange={(event) => setSearch(event.target.value)} />
                      {options.length > 0 && search && (
                        <div className="absolute z-10 mt-1 w-full rounded-lg border bg-popover p-1 shadow-lg">
                          {options.map((option) => (
                            <button key={option.id} onClick={() => link.mutate(option.id)} className="block w-full rounded-md px-3 py-1.5 text-start text-sm hover:bg-accent">
                              {option.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          {can('crm.object.delete') ? (
            <Button variant="ghost" className="text-destructive" onClick={() => window.confirm('Delete this record?') && remove.mutate()}>
              <Trash2 /> Delete
            </Button>
          ) : (
            <span />
          )}
          {data && can('crm.object.update') && (
            <Button variant="outline" onClick={() => onEdit(data.record)}>
              Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── On a CRM record ──────────────────────────────────────────────────────────

/** Custom records linked to a lead, contact, company or deal. */
export function LinkedObjectsCard({ entityType, entityId }: { entityType: RecordType; entityId: string }) {
  const { can } = usePermission();
  const { data: groups = [] } = useQuery({
    queryKey: ['objects', 'linked', entityType, entityId],
    queryFn: () =>
      apiGet<{ object: { key: string; singular: string; plural: string; fields: Field[] }; records: { id: string; name: string; data: Record<string, unknown> }[] }[]>(
        `/objects/linked/${entityType}/${entityId}`,
      ),
    enabled: can('crm.object.view'),
  });
  if (!groups.length) return null;
  return (
    <>
      {groups.map((group) => (
        <Card key={group.object.key}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Boxes className="h-4 w-4" /> {group.object.plural}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {group.records.length === 0 && (
              <p className="text-sm text-muted-foreground">
                None linked.{' '}
                <Link className="text-primary underline" to={`/objects/${group.object.key}`}>
                  Open {group.object.plural.toLowerCase()}
                </Link>
              </p>
            )}
            {group.records.map((record) => (
              <Link key={record.id} to={`/objects/${group.object.key}?open=${record.id}`} className="block rounded-lg border p-2 text-sm hover:bg-accent">
                <p className="font-medium">{record.name}</p>
                <p className="text-xs text-muted-foreground">
                  {group.object.fields
                    .slice(1)
                    .filter((field) => record.data[field.key] !== null && record.data[field.key] !== undefined && record.data[field.key] !== '')
                    .slice(0, 2)
                    .map((field) => displayValue(field, record.data[field.key]))
                    .join(' · ')}
                </p>
              </Link>
            ))}
          </CardContent>
        </Card>
      ))}
    </>
  );
}
